import { FC, useMemo, useRef } from 'react'
import _ from 'lodash'
import {
  Flex,
  Heading,
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Link
} from '@chakra-ui/react'
import numeral from 'numeral'

import RightPanelWrapper from 'shared/components/RightPanelWrapper'
import { useSelector } from 'model/hooks'
import { DictT, TourSlotT, ItemT } from 'shared/types/model'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PanelCartItem from 'shared/components/PanelCartItem'
import { faGrid2Plus, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { MpSdk } from 'shared/bundle/sdk'
import ResetDesignAlert, {
  IResetDesignAlert
} from 'shared/components/ResetDesignAlert'
import UnpricedItems from 'shared/components/UnpricedItems'

type Props = {
  sdk: MpSdk
  tourSlots: DictT<TourSlotT>
  onAddFurnitureClick: () => void
  onAddSetClick?: () => void
  openProductCard: (item: ItemT) => void
  addToRoom: (itemId: string) => void
  removeItem: (itemId: string) => void
  selectItem: (itemId: string) => void
  designName?: string
  onResetDesign?: () => void
}

const UserItemsPanel: FC<Props> = ({
  sdk,
  tourSlots,
  onAddFurnitureClick,
  onAddSetClick,
  openProductCard,
  addToRoom,
  removeItem,
  designName,
  onResetDesign,
  selectItem
}) => {
  const items = useSelector(state => state.items)
  const suppliers = useSelector(state => state.suppliers)
  const mattertags = useSelector(state => state.mattertags)
  const alertRef = useRef<IResetDesignAlert>(null)
  const partner = useSelector(state => state.partner)
  const sortedItems = useMemo(() => {
    const byItemId: DictT<TourSlotT[]> = _.groupBy(tourSlots, 'itemId')
    const itemsAr = _.map(byItemId, items => _.sortBy(items, 'createdAt'))
    return _.sortBy(itemsAr, ar => ar[0].createdAt)
  }, [tourSlots])

  const pricesVisible = useMemo(
    () => _.get(partner, 'pricesVisible', false),
    [partner]
  )

  const orderTotal = useMemo(() => {
    return _.sum(
      _.map(tourSlots, ts => {
        const item = _.get(items, ts.itemId)
        return _.get(item, 'price', 0)
      })
    )
  }, [tourSlots, items])

  const unpricedItems = useMemo(() => {
    const res: DictT<ItemT> = {}
    _.forEach(tourSlots, ts => {
      const item = _.get(items, ts.itemId)
      if (_.isNil(item.price)) {
        res[item.id] = item
      }
    })
    return _.values(res)
  }, [tourSlots, items])

  const renderCartItems = () => {
    if (items) {
      const res: any = []
      _.forEach(sortedItems, ar => {
        _.forEach(ar, (tourSlot, i) => {
          const item = _.get(items, tourSlot.itemId)
          if (item) {
            res.push(
              <PanelCartItem
                sdk={sdk}
                key={tourSlot.id}
                item={item}
                index={i}
                quantity={_.size(ar)}
                openProductCard={openProductCard}
                addToRoom={addToRoom}
                onRemove={() => removeItem(tourSlot.id)}
                onSelect={() => selectItem(tourSlot.id)}
                mattertagId={_.get(mattertags, tourSlot.id)}
                supplierName={
                  item.supplierId && _.get(suppliers, [item.supplierId, 'name'])
                }
                canEdit
                pricesVisible={_.get(partner, 'pricesVisible', false)}
              />
            )
          } else {
            console.warn('item of tour slot not found, ts:', tourSlot)
          }
        })
      })
      return res
    }
  }

  const resetDesign = () => {
    onResetDesign && onResetDesign()
  }

  const renderTotal = () => {
    if (pricesVisible) {
      return (
        <Flex align={'center'} justify='space-between' w='full'>
          <Text fontSize={'lg'} fontWeight='bold'>
            Total:
          </Text>
          <Text fontSize={'lg'} fontWeight='bold'>
            {numeral(orderTotal).format('$0,0.00')}
          </Text>
        </Flex>
      )
    }
  }

  return (
    <RightPanelWrapper show width={380}>
      <Flex flex={1} maxH='full' direction='column'>
        <Box pb={8} bg='white' p={4}>
          <Flex align='flex-start' justify={'space-between'}>
            <HStack align='flex-start' spacing={4}>
              <VStack align={'flex-start'}>
                <Heading size='md' color='black'>
                  {designName || 'Do It Yourself'}
                </Heading>
              </VStack>
            </HStack>
            <Button
              size='xs'
              variant={'ghost'}
              color='blue.500'
              onClick={() => alertRef.current?.open()}
            >
              Reset design
            </Button>
          </Flex>
        </Box>
        <VStack
          direction='column'
          align={'center'}
          justify='flex-start'
          flex={1}
          h='full'
          overflowX='hidden'
          overflowY={'auto'}
          sx={{ '.divider': { margin: 0 } }}
          divider={
            <Box
              className='divider'
              borderColor={'gray.300'}
              borderBottomWidth={1}
              w='full'
            />
          }
          // scrollSnapType={'y proximity'}
        >
          {renderCartItems()}
        </VStack>
        <Flex align='center' justify='end' w='full' p='2'>
          <Link
            href='https://www.youtube.com/playlist?list=PL_GNx7PMNVeh_dBVU5rR8_w8qh8iEruAW'
            isExternal
            color='gray.700'
            textDecoration='underline'
            fontSize='sm'
          >
            How-To Videos
          </Link>
        </Flex>
        <VStack bg='white' w='full' p={4} spacing={4}>
          {pricesVisible && <UnpricedItems items={unpricedItems} />}
          {renderTotal()}
          <HStack w='full'>
            <Button
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              w='full'
              // variant={'primary'}
              size='lg'
              colorScheme={'blue'}
              onClick={() => onAddFurnitureClick()}
            >
              Add item
            </Button>
            <Button
              id='customer-design-add-set-button'
              leftIcon={<FontAwesomeIcon icon={faGrid2Plus} />}
              w='full'
              variant={'outline'}
              size='lg'
              colorScheme={'blue'}
              onClick={() => onAddSetClick && onAddSetClick()}
            >
              Add set
            </Button>
          </HStack>
        </VStack>
        <ResetDesignAlert ref={alertRef} onApply={resetDesign} />
      </Flex>
    </RightPanelWrapper>
  )
}

export default UserItemsPanel
